import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { DiligenceDocumentRun, DiligenceDocumentRunEvent } from '../types';
import { isDocumentaryActivity } from '../server/diligenceDocuments/generationProgress';
import { formatActivityDuration } from '../utils/typingIndicatorState';
import { getActivityStartTime } from '../utils/documentActivityTiming';

export function getDocumentaryWorkStatus(run: DiligenceDocumentRun): boolean | undefined {
  if (run.documentaryWorkStarted === true || run.events.some(event => isDocumentaryActivity(event.stage))
    || isDocumentaryActivity(run.stage)) return true;
  return run.documentaryWorkStarted;
}

interface DiligenceDocumentRunCardProps {
  run: DiligenceDocumentRun;
  turnStartedAtMs?: number;
  precedingRun?: DiligenceDocumentRun;
}

type VisualTextSegment = {
  text: string;
  startIndex: number;
  isWhitespace: boolean;
};

const splitIntoVisualSegments = (text: string): VisualTextSegment[] => {
  let visualIndex = 0;
  return text.match(/\s+|\S+/g)?.map((segment) => {
    const item = {
      text: segment,
      startIndex: visualIndex,
      isWhitespace: /^\s+$/.test(segment)
    };
    visualIndex += Array.from(segment).length;
    return item;
  }) ?? [];
};

const AnimatedActivityText: React.FC<{ text: string }> = ({ text }) => {
  const [cycle, setCycle] = useState(0);
  const segments = useMemo(() => splitIntoVisualSegments(text), [text]);

  useEffect(() => {
    const interval = window.setInterval(() => setCycle((current) => current + 1), 2_800);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <>
      <span className="sr-only">{text}</span>
      <span key={`${text}-${cycle}`} aria-hidden="true">
        {segments.map((segment) => (
          segment.isWhitespace ? (
            <span key={`space-${segment.startIndex}`} className="whitespace-pre">{segment.text}</span>
          ) : (
            <span key={`segment-${segment.startIndex}`} className="inline-block whitespace-nowrap">
              {Array.from(segment.text).map((unit, index) => (
                <span
                  key={`unit-${segment.startIndex + index}`}
                  className="typing-soft-blur-unit inline-block whitespace-pre"
                  style={{ animationDelay: `${(segment.startIndex + index) * 14}ms` }}
                >
                  {unit}
                </span>
              ))}
            </span>
          )
        ))}
      </span>
    </>
  );
};

const ActivityLine: React.FC<{
  event: DiligenceDocumentRunEvent;
  animate: boolean;
}> = ({ event, animate }) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const hasDetails = event.pageCount !== undefined;
  const label = animate ? <AnimatedActivityText text={event.label} /> : event.label;

  return (
    <div className="text-[13px] leading-relaxed text-muted md:text-sm">
      <div className="flex min-h-6 items-start">
        {hasDetails ? (
          <button
            type="button"
            aria-expanded={detailsExpanded}
            onClick={() => setDetailsExpanded((current) => !current)}
            className="flex min-h-6 min-w-0 items-start gap-1 rounded text-left hover:text-ink focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2"
          >
            <span>{label}</span>
            <ChevronRight
              size={14}
              aria-hidden="true"
              className={`mt-0.5 shrink-0 transition-transform ${detailsExpanded ? 'rotate-90' : ''}`}
            />
          </button>
        ) : (
          <span>{label}</span>
        )}
      </div>
      {hasDetails && detailsExpanded && (
        <p className="mt-1 text-xs text-muted">
          {event.reviewedPages ?? 0} de {event.pageCount} páginas revisadas.
        </p>
      )}
    </div>
  );
};

const Timeline: React.FC<{ run: DiligenceDocumentRun; live: boolean }> = ({ run, live }) => {
  const events = run.events.filter((event) => event.stage !== 'received').reduce<DiligenceDocumentRunEvent[]>((visible, event) => {
    const previous = visible.at(-1);
    if (previous?.stage === event.stage && previous.label === event.label
      && previous.status !== 'failed' && previous.status !== 'cancelled'
      && event.status !== 'failed' && event.status !== 'cancelled') {
      visible[visible.length - 1] = { ...event, sequence: previous.sequence,
        status: previous.status === 'active' || event.status === 'active' ? 'active' : event.status };
    } else {
      visible.push(event);
    }
    return visible;
  }, []);
  if (events.length === 0) return null;
  // The animation signals the ongoing turn, even after its latest tool finishes.
  const latestEvent = events.at(-1);
  const animatedIndex = live && latestEvent?.status !== 'failed' && latestEvent?.status !== 'cancelled'
    ? events.length - 1 : -1;

  return (
    <ol className="space-y-3">
      {events.map((event, index) => {
        const activityIsLive = index === animatedIndex;
        return (
          <li key={event.sequence}>
            <div {...(activityIsLive ? { 'aria-live': 'polite', 'aria-atomic': true } : {})}>
              <ActivityLine event={event} animate={activityIsLive} />
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const DiligenceDocumentRunCard: React.FC<DiligenceDocumentRunCardProps> = ({ run: currentRun, turnStartedAtMs, precedingRun }) => {
  const run = useMemo(() => precedingRun ? {
    ...currentRun,
    ...(currentRun.documentaryWorkStarted === true || precedingRun.documentaryWorkStarted === true
      ? { documentaryWorkStarted: true } : {}),
    events: [...precedingRun.events.filter((event) => event.stage !== 'received').map((event) => ({ ...event,
      status: event.status === 'active' ? 'completed' as const : event.status })),
      ...currentRun.events].map((event, index) => ({ ...event, sequence: index + 1 }))
  } : currentRun, [currentRun, precedingRun]);
  const [expanded, setExpanded] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const isRunning = run.status === 'running';
  const hasActivities = run.events.some((event) => event.stage !== 'received');
  const runStartedAtMs = Number.isFinite(run.startedAtMs)
    ? run.startedAtMs : run.events.at(0)?.atMs ?? Date.now();
  const turnStart = precedingRun ? getActivityStartTime(precedingRun, turnStartedAtMs)
    : currentRun.attemptStartedAtMs !== undefined ? getActivityStartTime(currentRun, turnStartedAtMs) : turnStartedAtMs;
  const startedAtMs = Number.isFinite(turnStart) && Number(turnStart) <= runStartedAtMs
    ? Number(turnStart) : runStartedAtMs;
  const finishedAtMs = Number.isFinite(run.finishedAtMs)
    ? Number(run.finishedAtMs) : run.events.at(-1)?.atMs ?? startedAtMs;
  const duration = formatActivityDuration(Math.max(0, finishedAtMs - startedAtMs));
  const documentaryWork = getDocumentaryWorkStatus(run);
  const summary = documentaryWork === true ? 'Ha trabajado durante'
    : documentaryWork === false ? 'Ha pensado durante' : 'Tiempo transcurrido:';
  const hasCompleteTiming = (turnStart !== undefined && startedAtMs === turnStart)
    || run.timingComplete === true
    || (run.timingComplete !== false && run.events.at(0)?.sequence === 1);

  useEffect(() => {
    setExpanded(false);
  }, [isRunning, run.startedAtMs]);

  useEffect(() => {
    if (!isRunning) return undefined;
    setNowMs(Date.now());
    const interval = window.setInterval(() => setNowMs(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, [isRunning]);

  if (isRunning) {
    const activeDuration = formatActivityDuration(Math.max(0, nowMs - startedAtMs));
    return (
      <section aria-label="Trabajo de Juris" className="mb-4 w-full px-1 text-left">
        <p className="mb-3 text-sm font-medium text-ink" role="status">En proceso desde hace {activeDuration}</p>
        {hasActivities ? <Timeline run={run} live /> : (
          <p className="text-[13px] leading-relaxed text-muted md:text-sm">
            <AnimatedActivityText text="Pensando" />
          </p>
        )}
      </section>
    );
  }

  return (
    <section aria-label="Trabajo de Juris" className="mb-4 w-full px-1 text-left">
      {hasActivities ? (
        <button type="button" aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          className="flex min-h-8 items-center gap-1 rounded text-sm font-medium text-ink hover:text-ink focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2">
          <span>{summary} {hasCompleteTiming ? '' : 'al menos '}{duration}</span>
          <ChevronRight size={16} aria-hidden="true"
            className={`shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
      ) : (
        <p className="text-sm font-medium text-ink">
          {summary} {hasCompleteTiming ? '' : 'al menos '}{duration}
        </p>
      )}
      {expanded && <div className="mt-3"><Timeline run={run} live={false} /></div>}
    </section>
  );
};

export default DiligenceDocumentRunCard;
